/**
 * The Code tab's text (plan 7.3). No route answers a page's stored source
 * as it is: `editor-source` is the closest (the stored page numbered for the
 * editor, owned images inlined, the editor's content security policy). The
 * Code tab shows it without the editor's additions: the `data-tb` numbers
 * and the editor policy are removed (the server writes the page's own policy
 * back on save), and every inline image longer than a line is folded into a
 * short `tabbio-inline-image:<n>` placeholder so the code stays readable.
 * Saving unfolds the placeholders again.
 */

const EDITOR_ID = /\sdata-tb="e\d{1,6}"/g;
const POLICY = /<meta\s+http-equiv="Content-Security-Policy"\s+content="[^"]*"\s*\/?>/gi;
const INLINE_IMAGE = /(\ssrc=")(data:image\/[a-z0-9.+-]+;base64,[A-Za-z0-9+/=]+)(")/gi;
const PLACEHOLDER = /tabbio-inline-image:(\d{1,4})/g;

/** Inline images shorter than this stay in the code as they are. */
const FOLD_MIN_CHARS = 200;

export type PageCode = {
  code: string;
  /** Placeholder number to the image it stands for. */
  images: ReadonlyMap<number, string>;
};

export function pageCodeFromEditorSource(html: string): PageCode {
  const images = new Map<number, string>();
  const known = new Map<string, number>();
  const code = html
    .replace(EDITOR_ID, '')
    .replace(POLICY, '')
    .replace(INLINE_IMAGE, (whole, start: string, data: string, end: string) => {
      if (data.length < FOLD_MIN_CHARS) return whole;
      let index = known.get(data);
      if (index === undefined) {
        index = images.size + 1;
        known.set(data, index);
        images.set(index, data);
      }
      return `${start}tabbio-inline-image:${index}${end}`;
    });
  return { code, images };
}

/** The page to save: placeholders back to their images (an unknown one stays as typed). */
export function pageSourceForSave(code: string, images: ReadonlyMap<number, string>): string {
  return code.replace(PLACEHOLDER, (whole, index: string) => images.get(Number(index)) ?? whole);
}
